import { fetchWithChatFeedback } from '../api/chatFeedbackRequired';
import { Dialog } from './FarChat/ChatFeedbackOverlays';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, TextField, Typography } from '@mui/material';
import { Delete as DeleteIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { shareAndNotifyMarketResearchCounterparts, type MarketResearchNotificationContext, type MarketResearchNotificationRequestResult } from '../api/workflowNotifications';
import { toUserFacingError } from '../utils/userFacingError';
import { useToast } from './Toast/ToastProvider';

interface Props {
  flowId: string | null;
  context: MarketResearchNotificationContext | null;
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
  /** Optional controller-bound commit used only by the Home Chat Questions handoff. */
  onShareAndNotify?: (emails: string[], requestId: string) => Promise<MarketResearchNotificationRequestResult>;
}
type ShareRole = 'ra_customer' | 'contracting' | 'ce' | 'dcma';
const roleCopy: Record<ShareRole, string> = { ra_customer: 'RA / Customer', contracting: 'CO/CS', ce: 'CE', dcma: 'DCMA' };
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const requestId = () => typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `request-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
const noCounterpart = (context: MarketResearchNotificationContext) => context === 'questions' ? 'This workflow is not shared with any Customers.' : 'This workflow is not shared with any CO/CS.';
const receipt = (context: MarketResearchNotificationContext, users?: string[]) => {
  if (context === 'product_discovery_review') return 'Notification created for contracting review.';
  const unique = Array.from(new Set((users || []).filter((user) => typeof user === 'string' && user.length > 0)));
  if (!unique.length) return 'Notification created.';
  if (unique.length === 1) return `Notification created for ${unique[0]}.`;
  if (unique.length === 2) return `Notification created for ${unique[0]} and ${unique[1]}.`;
  return `Notification created for ${unique.slice(0, -1).join(', ')}, and ${unique.at(-1)}.`;
};

/** MR-only queued sharing. Generic Manage Workflows sharing remains unchanged. */
const MarketResearchNotifyShareDialog: React.FC<Props> = ({ flowId, context, open, onClose, onShareAndNotify, readOnly = false }) => {
  const accessRef = useRef({ flowId, context, open, readOnly });
  accessRef.current = { flowId, context, open, readOnly };
  const { showError, showInfo, showSuccess } = useToast();
  const [shared, setShared] = useState<string[]>([]);
  const [suggestion, setSuggestion] = useState<{ name: string; email: string } | null>(null);
  const [pending, setPending] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [roles, setRoles] = useState<Record<string, ShareRole>>({});
  const [loading, setLoading] = useState(false);
  const [lookingUpRoles, setLookingUpRoles] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const commitRequestId = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !flowId) return undefined;
    let active = true;
    setShared([]); setPending([]); setRoles({}); setEmailInput(''); setError(null); setLoading(true);
    fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/share`, { credentials: 'include' })
      .then(async (response) => { if (!response.ok) throw new Error('Failed to load shared users'); return response.json(); })
      .then((data) => { if (active) setShared(Array.isArray(data.shared_emails) ? data.shared_emails : []); })
      .catch(() => { if (active) setError('Unable to load sharing details right now.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [flowId, open]);

  useEffect(() => {
    setSuggestion(null);
    if (!open || !flowId || readOnly || context === 'questions') return undefined;
    let active = true;
    fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/market-research/flight-chief-suggestion`, { credentials: 'include' })
      .then(async response => response.ok ? response.json() : null)
      .then(data => {
        const value = data?.suggestion;
        if (active && typeof value?.name === 'string' && typeof value?.email === 'string' && EMAIL_REGEX.test(value.email)) {
          setSuggestion({ name: value.name, email: value.email.toLowerCase() });
        }
      })
      .catch(() => { /* A suggestion is optional; manual sharing remains available. */ });
    return () => { active = false; };
  }, [context, flowId, open, readOnly]);

  // Role classification begins only after Add makes an address visible in the
  // pending recipient list. Merely typing has no network/account lookup.
  useEffect(() => {
    if (!open || !flowId || pending.length === 0) { setLookingUpRoles(false); return undefined; }
    let active = true;
    setLookingUpRoles(true);
    fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/share/roles`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emails: pending }), credentials: 'include' })
      .then(async (response) => { if (!response.ok) throw new Error('Role lookup failed'); return response.json(); })
      .then((data) => {
        if (!active) return;
        const known = new Set<ShareRole>(['ra_customer', 'contracting', 'ce', 'dcma']);
        const next: Record<string, ShareRole> = {};
        for (const row of Array.isArray(data?.roles) ? data.roles : []) {
          if (!row || typeof row !== 'object') continue;
          const candidate = row as { email?: unknown; role?: unknown };
          const email = typeof candidate.email === 'string' ? candidate.email.trim().toLowerCase() : '';
          if (pending.includes(email) && typeof candidate.role === 'string' && known.has(candidate.role as ShareRole)) next[email] = candidate.role as ShareRole;
        }
        setRoles(next);
      })
      .catch(() => { if (active) setError('Unable to confirm account roles right now.'); })
      .finally(() => { if (active) setLookingUpRoles(false); });
    return () => { active = false; };
  }, [flowId, open, pending]);

  const addPending = () => {
    const emails = Array.from(new Set(emailInput.split(',').map((email) => email.trim().toLowerCase()).filter(Boolean)));
    const invalid = emails.filter((email) => !EMAIL_REGEX.test(email));
    if (invalid.length) { setError(`Invalid email address${invalid.length > 1 ? 'es' : ''}: ${invalid.join(', ')}`); return; }
    if (!emails.length) return;
    setError(null);
    setPending((existing) => [...existing, ...emails.filter((email) => !existing.includes(email) && !shared.includes(email))]);
    setEmailInput('');
  };

  const commit = useCallback(async () => {
    if (accessRef.current.readOnly || !accessRef.current.open || accessRef.current.flowId !== flowId || accessRef.current.context !== context || !flowId || !context || !pending.length || committing) return;
    setCommitting(true); setError(null);
    const opaqueRequestId = commitRequestId.current ?? requestId();
    commitRequestId.current = opaqueRequestId;
    try {
      const result = onShareAndNotify
        ? await onShareAndNotify(pending, opaqueRequestId)
        : await shareAndNotifyMarketResearchCounterparts(flowId, context, pending, opaqueRequestId);
      commitRequestId.current = null;
      if (result.no_counterpart) { showInfo(noCounterpart(context)); return; }
      if (result.already_pending) showInfo(
        context === 'questions'
          ? 'Customers have already been notified.'
          : 'A notification is already pending.',
      );
      else showSuccess(receipt(context, result.notified_users));
      onClose();
    } catch {
      showError('Unable to share and create the notification right now. Please try again.');
    } finally { setCommitting(false); }
  }, [committing, context, flowId, onClose, onShareAndNotify, pending, showError, showInfo, showSuccess]);

  return <Dialog open={open} onClose={committing ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="mr-share-notify-title">
    <DialogTitle id="mr-share-notify-title">Share Flow</DialogTitle>
    <DialogContent>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{toUserFacingError(error)}</Alert>}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box> : shared.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Not shared with anyone yet</Typography> : <List dense>{shared.map((email) => <ListItem key={email}><ListItemText primary={email} /></ListItem>)}</List>}
      {suggestion && !shared.some(email => email.trim().toLowerCase() === suggestion.email) && !pending.includes(suggestion.email) && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', my: 1 }}>
        <Typography variant="body2">Suggested flight chief: {suggestion.name} ({suggestion.email})</Typography>
        <Button onClick={() => setPending(emails => [...emails, suggestion.email])} disabled={loading || committing || readOnly}>Add flight chief</Button>
      </Box>}
      {pending.length > 0 && <List dense aria-label="Pending users to share">{pending.map((email) => <ListItem key={email}><ListItemText primary={email} secondary={roles[email] ? roleCopy[roles[email]] : lookingUpRoles ? 'Checking account role…' : 'Account role unavailable'} /><ListItemSecondaryAction><IconButton edge="end" size="small" aria-label={`Remove ${email}`} onClick={() => setPending((emails) => emails.filter((candidate) => candidate !== email))} disabled={committing}><DeleteIcon fontSize="small" /></IconButton></ListItemSecondaryAction></ListItem>)}</List>}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}><TextField size="small" label="Email address(es)" value={emailInput} onChange={(event) => setEmailInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addPending(); } }} placeholder="user@example.com, another@example.com" helperText="Separate multiple emails with commas" fullWidth disabled={readOnly || committing || loading} /><Button variant="contained" onClick={addPending} disabled={readOnly || committing || loading || !emailInput.trim()} startIcon={<PersonAddIcon />} sx={{ whiteSpace: 'nowrap', mt: '1px' }}>Add</Button></Box>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={committing}>Cancel</Button><Button variant="contained" onClick={() => void commit()} disabled={readOnly || !pending.length || committing || loading} startIcon={committing ? <CircularProgress size={16} color="inherit" /> : undefined}>{committing ? 'Sharing and notifying…' : 'Share and Notify'}</Button></DialogActions>
  </Dialog>;
};

export default MarketResearchNotifyShareDialog;
